import styled, { keyframes } from "styled-components";
import { theme } from "../../constants/theme";

// FieldShell + FieldError live in Inputs/FieldShell so the /submit,
// /refund, and /cancel email inputs can share the same treatment.
// Re-exported here so VerifyAddressForm/index.js keeps its local import.
export { FieldShell, FieldError } from "../Inputs/FieldShell";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Outer panel for the confirm-property-details section. Designed to
// sit directly below the NeighborhoodContext panel so the two read
// as one continuous editorial document — same coral top hairline,
// same cream-on-paper rhythm, same lift shadow. The form itself is
// on white paper inside the panel so the inputs feel obviously
// editable; the panel chrome lives at the outer edge.
export const FormPanel = styled.section`
  position: relative;
  background: var(--seo-paper);
  border: 1px solid var(--seo-rule-soft);
  border-radius: var(--seo-r-lg);
  padding: 1.6rem 1.7rem 1.8rem;
  box-shadow: var(--seo-shadow-card);
  overflow: hidden;
  animation: ${fadeUp} 0.45s cubic-bezier(0.2, 0.8, 0.2, 1) both;

  /* Coral hairline matches NeighborhoodContext — gives the two
     stacked panels a shared identity without copying the cream
     gradient (which would fight the form inputs' white surface). */
  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      var(--seo-coral) 30%,
      var(--seo-coral) 70%,
      transparent 100%
    );
  }

  @media (max-width: 640px) {
    padding: 1.2rem 1.1rem 1.4rem;
    border-radius: var(--seo-r-md);
  }
`;

// Inline section heading for the panel — small editorial eyebrow
// styling that matches NeighborhoodContext's Eyebrow without the
// coral rule (the panel already carries the coral identity at the
// top edge, so the eyebrow inside reads quieter).
export const FormHeading = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin: 0 0 1.1rem;
  flex-wrap: wrap;

  h3 {
    margin: 0 !important;
    font-size: 1.05rem !important;
    font-weight: 700 !important;
    color: var(--seo-navy);
    letter-spacing: -0.012em;
  }

  small {
    color: ${theme.color.textMuted};
    font-size: 0.8rem;
    letter-spacing: 0.01em;
  }
`;

// Two-column body: property fields on the left (≥md), map on the
// right. Below md the map collapses above the fields — visual anchor
// before the data-entry ask. The map column is sticky inside the
// scroll container on big screens so it doesn't disappear while the
// user works through the form.
export const PanelBody = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.4rem;

  @media (min-width: 992px) {
    grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
    align-items: start;
  }
`;

// Right-rail map column. On small screens it just renders inline
// above the fields (grid order swap below). Flex column so an
// optional belowMapAddon (e.g. /verify's reassurance note) stacks
// directly under the map and shares the column rhythm.
export const MapColumn = styled.div`
  order: -1;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;

  @media (min-width: 992px) {
    order: 0;
  }
`;

export const MapContainer = styled.div`
  /* No top padding here — the parent grid already manages spacing,
     and on mobile the iframe sits flush below the address row. */
`;

export const MapIframe = styled.iframe`
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.md};
  box-shadow: ${theme.shadow.sm};
  display: block;
  width: 100%;
  height: 320px;

  @media (min-width: 992px) {
    height: 400px;
  }
`;

export const MapNote = styled.small`
  display: block;
  margin-top: 0.5rem;
  color: ${theme.color.textMuted};
  font-size: 0.78rem;
  line-height: 1.5;

  a {
    color: var(--seo-coral-text);
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;

    &:hover {
      color: var(--seo-navy);
    }
  }
`;

// Certification block — two checkboxes wrapped in a quiet
// card so they read as a deliberate attestation step rather
// than floating boilerplate.  Subtle (not loud) — the
// FormStep's coral hairline at the top of the panel already
// carries the brand mark, so the certify card just needs to
// quietly group the checkboxes.
export const CertifyBlock = styled.div`
  /* Slight horizontal inset so the card breathes inside the
     FormStep's content area instead of running edge-to-edge.
     Padding-left is the largest of the four because the
     coral rule and the checkboxes both anchor on the left,
     and they need real breathing room from the card's edge. */
  margin: 1.5rem 0.25rem 0;
  padding: 1.15rem 1.4rem 1.2rem 2.2rem;
  background: rgba(253, 251, 247, 0.65);
  border: 1px solid var(--seo-rule);
  border-radius: var(--seo-r-md);
  position: relative;

  /* Quieter coral left rule — 2px instead of 3px and at
     lower opacity so the accent reads as a subtle gate
     marker, not a loud chrome element. */
  &::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 2px;
    background: var(--seo-coral);
    border-top-left-radius: var(--seo-r-md);
    border-bottom-left-radius: var(--seo-r-md);
    opacity: 0.55;
  }

  .form-group {
    margin-bottom: 0.55rem;
  }
  .form-group:last-child {
    margin-bottom: 0;
  }

  .certify-information-label {
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
    color: ${theme.color.text};
    font-size: 0.93rem;
    font-weight: 500;
    line-height: 1.5;
    cursor: pointer;
    letter-spacing: -0.003em;
  }

  .certify-information-label input[type="checkbox"] {
    margin-top: 0.22rem;
    flex: 0 0 auto;
    accent-color: var(--seo-coral);
    width: 1.05rem;
    height: 1.05rem;
  }
`;

export const SubmitWrapper = styled.div`
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "default")};
  margin-top: 1.6rem;
  display: flex;
  justify-content: center;

  /* Self-contained CTA styling.  Used to inherit from FormCard's
     wrapper-level .btn rules; now that /verify renders without
     FormCard, the button has to carry its own treatment.  Coral
     gradient ties it to the dossier/verify page palette. */
  .btn {
    appearance: none;
    border: none;
    background: linear-gradient(180deg, var(--seo-coral) 0%, var(--seo-coral-text) 100%);
    color: #fff;
    font-family: var(--font-body);
    font-size: 0.98rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-transform: none;
    padding: 0.85rem 1.6rem;
    border-radius: ${theme.radius.pill};
    box-shadow: 0 6px 18px rgba(168, 90, 58, 0.28),
      0 1px 0 rgba(255, 255, 255, 0.4) inset;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    transition:
      transform 0.15s cubic-bezier(0.2, 0.8, 0.2, 1),
      box-shadow 0.2s cubic-bezier(0.2, 0.8, 0.2, 1),
      filter 0.2s ease;
  }
  .btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(168, 90, 58, 0.34),
      0 1px 0 rgba(255, 255, 255, 0.4) inset;
    filter: brightness(1.04);
    color: #fff;
  }
  .btn:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(168, 90, 58, 0.24);
  }
  .btn:focus-visible {
    outline: 3px solid rgba(168, 90, 58, 0.4);
    outline-offset: 2px;
  }
  .btn:disabled,
  .btn.not-allowed {
    background: var(--seo-rule);
    color: var(--seo-slate-mute);
    box-shadow: none;
    cursor: not-allowed;
    filter: none;
  }
  .btn .fa {
    font-size: 0.78rem;
    line-height: 1;
  }
`;
